import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import SquareIcon from '@mui/icons-material/Square'
import Grid from '@mui/material/Grid2'
import { Add } from '@mui/icons-material'
import {
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { RootState } from 'store'
import { Button, Card, Input } from 'components'
import {
  Axios,
  getAttributeWarnings,
  sortByOrder,
  utilizeSilosData,
  validateAttributes,
} from 'utils'
import {
  BinColor,
  EquinomBin,
  ISilo,
  PaginationResponseType,
  TAttribute,
} from 'types'
import { useWheatCodesService } from 'services'
import { CrudAction, Entity } from 'types/permissions'
import { usePageHeader, usePermissions } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import { useSilosService } from 'services/silosService'
import Dropdown from 'components/ui/Dropdown/Dropdown'
import { getRangeTooltipMessage } from 'utils/helpers/validationFunctions'
import useConfirmModal from './components/dialogs/useConfirmModal'
import { SiloWrapper } from './components/silos/SiloWrapper'
import useEditSiloModal from './components/dialogs/useEditSiloModal'
import {
  CardGrid,
  ColumnsBox,
  Container,
  ContentHeader,
  ContentHeaderItem,
  IconContainer,
  Info,
  InfoItem,
  ListItem,
  MainContentWrapper,
  MainGrid,
  SideContent,
  Silos,
  StackComponent,
} from './EquinomBins.styled'
import * as sxStyles from './EquinomBins.style'
import { useSystemSettings } from '../../../../utils/hooks/useSystemSettings'

const fetchBins = async () => {
  const response = await Axios.get<PaginationResponseType<EquinomBin>>('/bins')
  return response?.data
}

const fetchBinColors = async () => {
  const response =
    await Axios.get<PaginationResponseType<BinColor>>('/bins/colors')
  return response?.data
}

const fetchSilos = async () => {
  // TODO: Type me
  const response = await Axios.get<PaginationResponseType<ISilo>>('/silos')
  return response?.data
}

const updateBin = async ({
  binId,
  data,
}: {
  binId: string
  data: EquinomBin
}) => {
  const response = await Axios.put(`bins/${binId}`, data)
  return response?.data
}

export const EquinomBins = () => {
  const theme = useTheme()
  const { checkPermission } = usePermissions()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const [isChangedWithoutSave, setIsChangedWithoutSave] =
    useState<boolean>(false)

  const [attributesState, setAttributesState] = useState<TAttribute[]>()
  const [attributesValidationErrors, setAttributesValidationErrors] = useState<
    Record<string, Record<string, string>>
  >({})
  const [attributeWarnings, setAttributeWarnings] = useState<
    Record<
      string,
      {
        range_min?: React.ReactNode
        range_max?: React.ReactNode
        range_avg?: React.ReactNode
      }
    >
  >({})
  const { isSideNavOpen } = useSelector((state: RootState) => state.sideNav)
  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )
  const { createSilo } = useSilosService()
  const { getWheatCodeAttributes } = useWheatCodesService()

  const { data: binsList, refetch: refetchBins } = useQuery(
    'fetch-bins',
    fetchBins,
    {}
  )
  const { data: colorsData } = useQuery('fetch-bin-colors', fetchBinColors)
  const { data: silosData, refetch: refetchSilos } = useQuery(
    'fetch-silos',
    fetchSilos,
    {
      enabled: checkPermission(Entity.SILOS, CrudAction.GET),
      refetchOnWindowFocus: false,
    }
  )
  const updateBinMutation = useMutation(updateBin, {
    onSuccess: () => {
      successToast('Bin attributes has been updated')
      setIsChangedWithoutSave(false)
      void queryClient.invalidateQueries(['fetch-bins'])
      // void queryClient.invalidateQueries(['fetch-silos'])
    },
    onError: () => {
      toast.error('Error updating bin attributes')
    },
  })

  const [activeItem, setActiveItem] = useState<number>(0)
  const [activeColor, setActiveColor] = useState<any>()

  const { isEnabled: displayStorage } = useSystemSettings('SHOW_WHEAT_STORAGE')

  const filteredBinsList = binsList?.results.filter((bin) => {
    return bin.code === '-1' ? displayStorage : true
  })
  const activeBin = filteredBinsList?.[activeItem]

  const [renderBinChangeConfirm, toogleBinChangeConfirm] = useConfirmModal({
    onConfirm: () => {},
    title: 'Attention!',
    text: 'You have change a bin attributes. Are you sure you would like to continue without saving? ',
  })

  useEffect(() => {
    if (attributesState) {
      const warnings = getAttributeWarnings(attributesState, validationRules)
      setAttributeWarnings(warnings)
    }
  }, [attributesState, validationRules])

  const handleUpdate = async (
    bin: EquinomBin,
    attributes: TAttribute[],
    colorId
  ) => {
    // Validate attributes using the same function as in handleChange
    const validationErrors = await validateAttributes(
      attributes,
      validationRules
    )

    // Check if there are any validation errors
    if (Object.keys(validationErrors).length > 0) {
      // If there are errors, set them and return early
      setAttributesValidationErrors(validationErrors)
      return
    }

    // If no errors, continue with the update
    setAttributesValidationErrors({})

    const color = colorsData.results.find((item) => item?.id === colorId)

    updateBinMutation.mutate({
      binId: bin?.id?.toString(),
      data: { ...bin, attributes, color, color_id: colorId },
    })
  }

  const colorsOptions = useMemo(
    () =>
      colorsData?.results?.map(({ id, name, hex_code }) => ({
        value: id,
        label: name,
        icon: (
          <IconContainer color={hex_code}>
            <SquareIcon />
          </IconContainer>
        ),
      })),
    [colorsData?.results]
  )

  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )
  const queryClient = useQueryClient()

  const createSiloMutation = useMutation(createSilo, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['silos_list'])
      successToast('Silo created')
      reFetchingSilos()
    },
    onError: (error) => {
      toast.error('An error accured while creating silo')
      console.error('Failed to create silo:', error)
    },
  })

  const [addModal, toggleAddModal] = useEditSiloModal({
    initialData: {
      name: '',
      company_id: companyId,
    },
    onConfirm: async (data: ISilo) => {
      createSiloMutation.mutate({
        ...data,
        company_id: companyId,
      })
    },
    mode: 'add',
  })

  const reFetchingSilos = () => {
    refetchSilos()
  }

  useEffect(() => {
    if (activeBin?.attributes) {
      // TODO: revise
      setAttributesState(JSON.parse(JSON.stringify(activeBin?.attributes)))
      setActiveColor(activeBin.color.id)

      // Clear validation errors when switching bins
      setAttributesValidationErrors({})
    }
  }, [activeBin?.attributes])

  // TYPE ME
  const utilizedSilos: any[] = utilizeSilosData(
    silosData?.results,
    activeBin?.silos
  )

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: string
  ) => {
    const newState = [...attributesState]
    const value = e?.target?.value
    const key = e?.target?.name

    const newAttributes = newState?.map((item) => {
      if (item.name === name) {
        item[key] = value
        return item
      }
      return item
    })
    setAttributesState(newAttributes)
    setIsChangedWithoutSave(true)

    // Wait for validation to complete and update errors state
    const errors = await validateAttributes(newAttributes, validationRules)

    setAttributesValidationErrors(errors)
  }

  const handleRestore = () => {
    if (activeBin?.attributes) {
      setAttributesState(JSON.parse(JSON.stringify(activeBin?.attributes)))
      setActiveColor(activeBin.color.id)
    }
  }

  const sortedBins = filteredBinsList?.sort(function (a, b) {
    return a?.id - b?.id
  })

  const hasColorChanged = (staticColor: string, stateColor: string) =>
    staticColor !== stateColor

  usePageHeader({
    headerTitle: 'Equinom Bins',
  })

  return (
    <Container
      sx={
        isSideNavOpen
          ? isMediumScreen
            ? sxStyles.navOpenSmallScreenContainer
            : sxStyles.navOpenLargeScreenContainer
          : sxStyles.navClosedContainer
      }
    >
      <Grid container sx={sxStyles.gridByPresentage}>
        <MainContentWrapper>
          <Grid
            container
            spacing={2}
            sx={sxStyles.gridByPresentage}
            direction={{ xs: 'row', lg: 'column' }}
          >
            <MainGrid size={isMediumScreen ? 12 : 2}>
              <SideContent>
                <ContentHeader>Equinom Bins</ContentHeader>
                {sortedBins?.map((item, index) => {
                  return (
                    <ListItem
                      key={item.id}
                      active={index === activeItem}
                      onClick={() => {
                        if (
                          (isChangedWithoutSave &&
                            !_.isEqual(
                              attributesState,
                              activeBin?.attributes
                            )) ||
                          hasColorChanged(activeBin?.color?.id, activeColor)
                        ) {
                          toogleBinChangeConfirm({
                            onProceed: () => {
                              setActiveItem(index)
                              void refetchBins()
                              void refetchSilos()
                            },
                          })
                          return
                        } else {
                          setActiveItem(index)
                          refetchBins()
                          refetchSilos()
                        }
                      }}
                    >
                      {item?.name}
                    </ListItem>
                  )
                })}
              </SideContent>
            </MainGrid>
            <MainGrid size={isMediumScreen ? 12 : 5}>
              <ColumnsBox>
                <ContentHeader>
                  <ContentHeaderItem>{activeBin?.name}</ContentHeaderItem>
                </ContentHeader>
                <Info>
                  <Dropdown
                    value={activeColor}
                    options={colorsOptions || []}
                    label="Color"
                    size="small"
                    searchable
                    onChange={(event) => {
                      setActiveColor(event.target.value)
                    }}
                  />

                  {sortByOrder(attributesState)?.map((attribute) => {
                    return (
                      <InfoItem key={attribute?.name}>
                        <Typography sx={sxStyles.nameTypographyWidth}>
                          {attribute?.name}
                        </Typography>
                        <Box sx={sxStyles.inputBoxWrapper}>
                          <Box sx={sxStyles.inputBoxWrapperWidth}>
                            <Tooltip
                              title={getRangeTooltipMessage(
                                validationRules,
                                attribute.name,
                                'range_min'
                              )}
                              arrow
                              placement="top"
                            >
                              <Input
                                type="text"
                                size="small"
                                name="range_min"
                                fullWidth
                                onChange={(e) =>
                                  handleChange(e, attribute?.name)
                                }
                                value={attribute.range_min}
                                disabled={
                                  !checkPermission(
                                    Entity.BINS,
                                    CrudAction.UPDATE
                                  )
                                }
                                error={
                                  !!attributesValidationErrors[attribute.name]
                                    ?.range_min
                                }
                                helperText={
                                  attributesValidationErrors[attribute.name]
                                    ?.range_min ||
                                  attributeWarnings[attribute.name]
                                    ?.range_min ||
                                  ''
                                }
                                sx={
                                  attributeWarnings[attribute.name]?.range_min
                                    ? sxStyles.rightHelperWarningTextStyle
                                    : sxStyles.rightHelperTextStyle
                                }
                              />
                            </Tooltip>
                          </Box>
                          <Box sx={sxStyles.dashWrapperBox}>-</Box>
                          <Box sx={sxStyles.inputBoxWrapperWidth}>
                            <Tooltip
                              title={getRangeTooltipMessage(
                                validationRules,
                                attribute.name,
                                'range_max'
                              )}
                              arrow
                              placement="top"
                            >
                              <Input
                                type="text"
                                size="small"
                                name="range_max"
                                fullWidth
                                onChange={(e) =>
                                  handleChange(e, attribute?.name.trim())
                                }
                                value={attribute.range_max}
                                disabled={
                                  !checkPermission(
                                    Entity.BINS,
                                    CrudAction.UPDATE
                                  )
                                }
                                error={
                                  !!attributesValidationErrors[attribute.name]
                                    ?.range_max
                                }
                                helperText={
                                  attributesValidationErrors[attribute.name]
                                    ?.range_max ||
                                  attributeWarnings[attribute.name]
                                    ?.range_max ||
                                  ''
                                }
                                sx={
                                  attributeWarnings[attribute.name]?.range_max
                                    ? sxStyles.leftHelperWarningTextStyle
                                    : sxStyles.leftHelperTextStyle
                                }
                              />
                            </Tooltip>
                          </Box>
                          <Box sx={sxStyles.inputBoxWrapperWidth} ml={'15px'}>
                            <Tooltip
                              title={
                                <Box>
                                  {getRangeTooltipMessage(
                                    validationRules,
                                    attribute.name,
                                    'range_min'
                                  )}
                                  {getRangeTooltipMessage(
                                    validationRules,
                                    attribute.name,
                                    'range_max'
                                  )}
                                </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <Input
                                type="text"
                                size="small"
                                name="range_avg"
                                fullWidth
                                onChange={(e) =>
                                  handleChange(e, attribute?.name.trim())
                                }
                                value={attribute.range_avg}
                                disabled={
                                  !checkPermission(
                                    Entity.BINS,
                                    CrudAction.UPDATE
                                  )
                                }
                                error={
                                  !!attributesValidationErrors[attribute.name]
                                    ?.range_avg
                                }
                                helperText={
                                  attributesValidationErrors[attribute.name]
                                    ?.range_avg ||
                                  attributeWarnings[attribute.name]
                                    ?.range_avg ||
                                  ''
                                }
                                sx={
                                  attributeWarnings[attribute.name]?.range_avg
                                    ? sxStyles.leftHelperWarningTextStyle
                                    : sxStyles.rightHelperTextStyle
                                }
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      </InfoItem>
                    )
                  })}
                </Info>
              </ColumnsBox>
            </MainGrid>
            <Grid
              size={isMediumScreen ? 12 : 5}
              sx={
                isMediumScreen
                  ? sxStyles.smallScreenGrid
                  : sxStyles.largeScreenGrid
              }
            >
              <ColumnsBox>
                <ContentHeader>
                  <ContentHeaderItem>Linked Silos</ContentHeaderItem>
                  {checkPermission(Entity.SILOS, CrudAction.CREATE) && (
                    <Button startIcon={<Add />} onClick={toggleAddModal}>
                      Add Silo
                    </Button>
                  )}
                </ContentHeader>
                <Silos>
                  {utilizedSilos?.map((item) => (
                    <SiloWrapper
                      key={item.id}
                      item={item}
                      data={filteredBinsList}
                      activeItem={activeItem}
                      reFetchingSilos={reFetchingSilos}
                    />
                  ))}
                </Silos>
              </ColumnsBox>
            </Grid>
          </Grid>
        </MainContentWrapper>
        {checkPermission(Entity.BINS, CrudAction.UPDATE) && (
          <CardGrid size={12}>
            <Card
              sx={
                isSideNavOpen
                  ? isMediumScreen
                    ? sxStyles.navOpenSmallScreenCard
                    : sxStyles.navOpenLargeScreenCard
                  : isMediumScreen
                    ? sxStyles.navClosedSmallScreenCard
                    : sxStyles.navClosedLargeScreenCard
              }
            >
              <StackComponent>
                <Button
                  variantType="secondary"
                  onClick={handleRestore}
                  sx={sxStyles.restoreBtn}
                  disabled={_.isEqual(
                    attributesState,
                    JSON.parse(JSON.stringify(activeBin?.attributes ?? {}))
                  )}
                >
                  Discard Changes
                </Button>
                <Button
                  onClick={() =>
                    handleUpdate(activeBin, attributesState, activeColor)
                  }
                  sx={sxStyles.updateBtn}
                >
                  Update
                </Button>
              </StackComponent>
            </Card>
          </CardGrid>
        )}
      </Grid>
      {renderBinChangeConfirm}
      {addModal}
    </Container>
  )
}
